import { useState, useEffect, useRef, useCallback } from 'react'

const WORD_INTERVAL_MS = 30

/**
 * Typewriter animation for streaming messages.
 *
 * Tracks a cursor position into `content`. During streaming, the cursor advances
 * one word at a time (~30ms per word). The displayed text is always
 * `content.slice(0, cursor)` — a direct substring of the real content.
 *
 * No word splitting or concatenation — avoids whitespace bugs entirely.
 */
export function useStreamingAnimation(content: string, isStreaming: boolean): string {
  const [displayed, setDisplayed] = useState(content)
  const cursorRef = useRef(content.length)
  const contentRef = useRef(content)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  contentRef.current = content

  const advance = useCallback(() => {
    const text = contentRef.current
    let cursor = cursorRef.current

    if (cursor >= text.length) {
      timerRef.current = null
      return
    }

    // Advance cursor past whitespace, then past the next word
    while (cursor < text.length && /\s/.test(text[cursor])) cursor++
    while (cursor < text.length && /\S/.test(text[cursor])) cursor++

    cursorRef.current = cursor
    setDisplayed(text.slice(0, cursor))

    if (cursor < text.length) {
      timerRef.current = setTimeout(advance, WORD_INTERVAL_MS)
    } else {
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!isStreaming) {
      // Not streaming — show full content, stop animation
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      cursorRef.current = content.length
      setDisplayed(content)
      return
    }

    // Streaming — kick off animation if cursor is behind content
    if (content.length > cursorRef.current && !timerRef.current) {
      timerRef.current = setTimeout(advance, WORD_INTERVAL_MS)
    }
  }, [content, isStreaming, advance])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return displayed
}
